import { useState } from 'react';
import { Box, IconButton, Chip, Stack, Tooltip } from '@mui/material';
import { HelpOutlineOutlined as QuestionIcon } from '@mui/icons-material';
import { Text } from 'mbd-components/text';
import { BrandedDialog } from 'mbd-components/branded-dialog';
import { colors } from 'helper/colors';
import { getGeloLocationDisplayString } from 'helper/utils';
import { explanations } from 'helper/explanations';

export function NonEditableFeedDetails(props){
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [infoDialogTitle, setInfoDialogTitle] = useState('');
  const [infoDialogText, setInfoDialogText] = useState('');

  const NonEditableTextField = (props) => 
    <Box  mr={4} mt={4} sx={{display:'flex', flexDirection: props.horizontal ? 'row' : 'column', alignItems: props.horizontal ? 'center' : 'flex-start'}} >
      <Text variant='fieldHeading' color='brand-light'>
        {props.heading}
        {
            props.showExplanation &&
            <IconButton
              ml={1}
              onClick={() => {setOpenInfoDialog(true); 
                setInfoDialogTitle(props.explanationTitle); 
                setInfoDialogText(props.explanationDescription)
              }}
            >
              <Tooltip title={`What is ${props.heading.toLowerCase()}?`}>
                <QuestionIcon sx={{color:colors['gray-lighter']}}/>
              </Tooltip>
            </IconButton>
          }
      </Text>
      <Text variant='fieldValue' color='gra' sx={{marginLeft: props.horizontal ? 2 : 0, marginTop: props.horizontal ? 0 : 2}}>
        {props.value}
      </Text>
    </Box>

  const NonEditableSelections = (props) =>
    <Box>
      <Box my={2}>
        <Text variant='fieldHeading' color='brand-light'>{props.heading}</Text>
      </Box>
      {
        props.selections.length > 0 ? 
          <Stack direction='row' spacing={1} sx={{flexWrap: 'wrap', gap: '16px'}}>
            {props.selections.map((selection) => <Chip  label={selection} variant='outlined' key={selection} sx={{color:'#97979A'}}/>)}
          </Stack>
        : 
          <Text variant='fieldValue' color='gray-light'>None</Text>
      }
    </Box>

  const Promotion = (props) =>

      <Box>
        <NonEditableTextField horizontal={true} heading="Promotion Type" value={props.promotionTypeValue}/> 
          <Box>
            <Box display="flex" flexDirection={'row'}>
              <NonEditableTextField horizontal={true} heading="Feed ID" value={props.selectedPromotionFeedId}/>
              <NonEditableTextField horizontal={true} heading="Feed Name" value={props.selectedPromotionFeedName}/>
            </Box>
            <Box display="flex" flexDirection={'row'}>
              <NonEditableTextField horizontal={true} heading="Promotion Name" value={props.promotionName}/>
              <NonEditableTextField horizontal={true} heading="Percentage" value={props.promotionPercentage}/>
            </Box>
          </Box>

      </Box>
  
  return (
    <Box my={1}>
      <BrandedDialog 
        open={openInfoDialog} 
        title={infoDialogTitle} 
        texts={infoDialogText} 
        onClose={() => setOpenInfoDialog(false)} 
        buttonText="Got It 👀 "
      />
      <Box display="flex" flexDirection={'row'} alignItems={'center'}>
        <Box>
          <Text variant='h1'>Feed Info</Text>
        </Box>
      </Box>
      <NonEditableTextField horizontal={true} heading="Endpoint" value={props.endpoint}/>
      {
        props.description && props.description !== '' &&
        <NonEditableTextField horizontal={true} heading="Description" value={props.description}/>
      }
      <Box mt={4}>
        <Text variant='h1'>1. Candidate Generation</Text>
        <NonEditableTextField horizontal={true} heading="Time Period" value={`${props.timePeriodDays} days`}/>
        <Box mt={4}>
          <Text variant='h2'>Include Params</Text>
          <NonEditableSelections heading="Source App FIDs" selections={props.includeAppFids}/>
          <NonEditableSelections heading="Geo Locations" selections={props.includeGeoLocations.map( loc => getGeloLocationDisplayString(loc))}/>
          <NonEditableSelections heading="Author IDs" selections={ props.includeAuthorIds }/>
          <NonEditableSelections heading="Channels" selections={props.channels}/>
          <NonEditableSelections heading="AI Labels" selections={props.includeAiLabels}/>
          <NonEditableSelections heading="Embed Domains" selections={props.embedDomains}/>
          <NonEditableSelections heading="Publication Types" selections={props.includePublicationTypes}/>
        </Box>
        <Box mt={4}>
          <Text variant='h2'>Exclude Params</Text>
          <NonEditableSelections heading="Author IDs" selections={props.excludeAuthorIds}/>
          <NonEditableSelections heading="AI Labels" selections={props.excludeAiLabels}/>
          <NonEditableSelections heading="Geo Locations" selections={props.excludeGeoLocations.map( loc => getGeloLocationDisplayString(loc))}/>
        </Box>
      </Box>
      <Box mt={4}>
        <Text variant='h1'>2. Ranking</Text>
        <NonEditableTextField horizontal={true} heading="Time Decay" value={props.timeDecay && props.timeDecay > 0 ? props.timeDecay : 'None'}  showExplanation={true} explanationTitle={explanations.timeDecay.title} explanationDescription={explanations.timeDecay.description}/>
      </Box>
      <Box mt={4}>
        <Text variant='h1'>3. Visibility Filters</Text>
        <NonEditableTextField horizontal={true} heading="Impression Count" value={props.impressionCount} showExplanation={true} explanationTitle={explanations.impressionCount.title} explanationDescription={explanations.impressionCount.description}/>
        <NonEditableTextField horizontal={true} heading="Minimum Like Count" value={props.minLikeCount || 0} showExplanation={true} explanationTitle={explanations.impressionCount.title} explanationDescription={explanations.impressionCount.description}/>
        <Box mt={4}>
          <Text variant='h2'>Feed Diversity</Text>
        </Box>
        <Box my={2}>
          {
            props.showFeedDiversitySection ?
              <>
                <NonEditableTextField horizontal={true} heading="Maximum Posts from Same Author" value={props.maxPostsPerAuthor > 0 ? props.maxPostsPerAuthor : 'None'} showExplanation={true} explanationTitle={explanations.maxPostsPerAuthor.title} explanationDescription={explanations.maxPostsPerAuthor.description}/>
                <NonEditableTextField horizontal={true} heading="Minimum Distance Between Posts from Same Author" value={props.minDistanceBetweenPostsFromSameAuthor > 0 ? props.minDistanceBetweenPostsFromSameAuthor : 'None'} showExplanation={true} explanationTitle={explanations.minDistanceBetweenPosts.title} explanationDescription={explanations.minDistanceBetweenPosts.description}/>
              </>
            :
            <Text variant='fieldValue' color='gray-light'>None</Text>
          }
        </Box>
      </Box>
      <Box mt={4}>
        <Text variant='h1'>4. Feed Construction</Text>
      </Box>
      <Box mt={4}>
        <Text variant='h2'>
          Promotions
          <IconButton
            ml={1}
            onClick={() => {setOpenInfoDialog(true); 
              setInfoDialogTitle(explanations.promotion.title); 
              setInfoDialogText(explanations.promotion.description)
            }}
          >
            <Tooltip title="What is promotion?">
              <QuestionIcon sx={{color:colors['gray-lighter']}}/>
            </Tooltip>
          </IconButton>
        </Text>
        {
          props.showPromotionSection ?
            <Promotion 
              promotionTypeValue={props.promotionTypeValue}
              selectedPromotionFeedId={props.selectedPromotionFeedId}
              selectedPromotionFeedName={props.selectedPromotionFeedName}
              promotionName={props.promotionName}
              promotionPercentage={props.promotionPercentage}
            /> 
          : 
            <Text variant='fieldValue' color='gray-light'>None</Text>
        }
      </Box>
      <Box mt={4}>
        <Text variant='h2'>
          Cold Start
          <IconButton
            ml={1}
            onClick={() => {setOpenInfoDialog(true); 
              setInfoDialogTitle(explanations.coldStart.title); 
              setInfoDialogText(explanations.coldStart.description)
            }}
          >
            <Tooltip title="What is cold start?">
              <QuestionIcon sx={{color:colors['gray-lighter']}}/>
            </Tooltip>
          </IconButton>
        </Text>
        {
          props.showColdStartSection ?  
            <Box mt={4}>
              <NonEditableTextField horizontal={true} heading="Feed ID" value={props.coldStartFeedId}/>
            </Box>
          :
            <Text variant='fieldValue' color='gray-light'>None</Text>
        }
      </Box>



    </Box>
  );
}